import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Badge } from "@/shared/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/shared/components/ui/sheet";
import { formatDateBR } from "@/shared/utils/datetime";
import { MemoryService } from "../services/memoryService";
import type { Memory, MemoryTarget } from "../types/MemoryTypes";

interface MemoryFactsSheetProps {
  target: MemoryTarget;
  memory: Memory | null;
  onOpenChange: (open: boolean) => void;
}

/** Os fatos que o grafo extraiu de uma memória, com o que já foi substituído (AB#87149). */
export const MemoryFactsSheet = ({ target, memory, onOpenChange }: MemoryFactsSheetProps) => {
  const { t } = useTranslation();
  const { data: facts = [], isLoading, isError } = useQuery({
    queryKey: ["memory-facts", memory?.id],
    queryFn: () => MemoryService.facts(target, memory!.id),
    enabled: !!memory,
  });

  return (
    <Sheet open={!!memory} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-4 sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{t("memory.facts.title")}</SheetTitle>
          <SheetDescription>{t("memory.facts.description")}</SheetDescription>
        </SheetHeader>

        {memory && <blockquote className="border-l-2 pl-3 text-sm text-muted-foreground">{memory.content}</blockquote>}

        {isLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <p className="text-sm text-destructive">{t("memory.facts.error")}</p>
        ) : facts.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("memory.facts.empty")}</p>
        ) : (
          <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
            {facts.map((fact) => (
              <li key={fact.id} className="space-y-1 rounded-md border p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={fact.current ? "success" : "secondary"} className="text-[10px]">
                    {t(fact.current ? "memory.facts.current" : "memory.facts.replaced")}
                  </Badge>
                  {fact.valid_at && (
                    <span className="text-xs text-muted-foreground">
                      {t("memory.facts.since", { date: formatDateBR(fact.valid_at) })}
                    </span>
                  )}
                </div>
                <p className={fact.current ? "" : "text-muted-foreground line-through"}>{fact.fact}</p>
              </li>
            ))}
          </ul>
        )}
      </SheetContent>
    </Sheet>
  );
};
